import {
  createUrl,
  removeUrlParams,
  updateUrlWithParam,
} from '@doda/common/src/utils/urlUtil';
import { queryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { useAppSelector } from '@doda/common/src/redux/hooks';
import { MouseEvent } from 'react';
import { makeStaticUrl } from '@doda/common/src/logics/staticUrl';
import { QUERY_PARAMS } from '@doda/common/src/constants/queryParams';
import { useRouter } from 'next/router';
import { PATH_NAME_ACTION, REMOVE_PARAM_LIST } from '../../constants';

/**
 * ページナビゲーションのURLを生成するカスタムフック
 * @param {Object} params
 * @param {string} params.baseUrl - ベースとなるURL
 * @param {boolean} params.hasNextPage - 次のページが存在するかどうか
 * @param {boolean} params.isPrsrt - 並べ替えが有効かどうか
 * @param {boolean} [params.isStaticUrl=false] - 静的URLを使用するかどうか（任意）
 * @returns {Object} ページ送りのURLとクリックハンドラー (previousPageHref: 前のページへのリンク, nextPageHref: 次のページへのリンク, onClickNextPageNav: 次のページへのリンククリック時のハンドラー, onClickPrePageNav: 前のページへのリンククリック時のハンドラー)
 */
export const useCreatePageNav = ({
  baseUrl,
  hasNextPage,
  isPrsrt,
  isStaticUrl = false,
}: {
  baseUrl: string;
  hasNextPage: boolean;
  isPrsrt: boolean;
  isStaticUrl?: boolean;
}) => {
  const { pathname } = useRouter();
  const queryParams = useAppSelector(queryParamsSelector);
  const { page = '1' } = queryParams;
  const currentPage = Number(page);
  const hasPreviousPage = currentPage > 1;

  const removedUrlForPage = removeUrlParams(baseUrl, REMOVE_PARAM_LIST);

  const baseUrlForPage = isStaticUrl
    ? createUrl({
        // 正規化されている場合
        href: makeStaticUrl(
          {
            ...queryParams,
            [QUERY_PARAMS.prsrt]: undefined,
            [QUERY_PARAMS.ss]: undefined,
            [QUERY_PARAMS.usrclk_searchList]: undefined,
            [QUERY_PARAMS.usrclk]: undefined,
            [QUERY_PARAMS.kwc]: undefined,
          },
          pathname + PATH_NAME_ACTION
        ),
      })
    : createUrl({
        href: removedUrlForPage,
      });

  const encodedBaseUrlForPage = baseUrlForPage.replace(/,/g, '%2C');

  let previousPageHref: undefined | string;
  if (currentPage === 2) {
    // 1ページ目へのリンクはpageパラメータを削除する
    previousPageHref = removeUrlParams(encodedBaseUrlForPage, ['page']);
  } else if (hasPreviousPage) {
    const previousPageNumber = currentPage - 1;
    previousPageHref = updateUrlWithParam(
      encodedBaseUrlForPage,
      'page',
      String(previousPageNumber)
    );
  }

  const nextPageHref = hasNextPage
    ? updateUrlWithParam(encodedBaseUrlForPage, 'page', String(currentPage + 1))
    : undefined;

  /**
   * リンク押下時にprsrtクエリを付与する
   * @param {MouseEvent<HTMLAnchorElement>} event - クリックイベント
   * @returns {void}
   */
  const onClickNextPageNav = (event: MouseEvent<HTMLAnchorElement>): void => {
    // isPrsrtを付与しない画面では処理終了
    if (!isPrsrt) return;
    if (nextPageHref) {
      event.currentTarget.href = createUrl({
        href: nextPageHref,
        params: { [QUERY_PARAMS.prsrt]: '1' },
      });
    }
  };

  /**
   * リンク押下時に正規化したURLが動的に変わってしまうのでイベントで制御
   * @param {MouseEvent<HTMLAnchorElement>} event - クリックイベント
   * @returns {void}
   */
  const onClickPrePageNav = (event: MouseEvent<HTMLAnchorElement>): void => {
    // isPrsrtを付与しない画面では処理終了
    if (!isPrsrt) return;
    if (previousPageHref) {
      event.currentTarget.href = createUrl({
        href: previousPageHref,
        params: { [QUERY_PARAMS.prsrt]: '1' },
      });
    }
  };

  return {
    previousPageHref,
    nextPageHref,
    onClickNextPageNav,
    onClickPrePageNav,
  };
};
